/**
 * Classic Site environment / CORS / cookie diagnostics — frontend only.
 *
 * Helps distinguish config mismatch, CORS blocking, and missing session cookies
 * when React (e.g. xd.themamahood.co) calls hoodie API on another origin.
 *
 * Never exposes cookie values, CSRF tokens, or auth headers.
 */

import { HOODIE_API_BASE_URL, isHoodieApiConfigured } from "../../../api/config";
import { getDjangoSiteOrigin } from "../auth/djangoOrigin";
import { getCsrfCookieDiagnostic, SESSION_PROFILE_PATH } from "../auth/sessionHelpers";

const RESPONSE_PREVIEW_MAX = 500;

/**
 * @typedef {'network'|'http_401'|'http_403'|'http_other'|'parse'|'unconfigured'|null} DiagnosticErrorCategory
 */

/**
 * @typedef {Object} EnvironmentSnapshot
 * @property {string|null} frontendOrigin
 * @property {string|null} backendOrigin
 * @property {string|null} apiBaseUrl
 * @property {string|null} sessionEndpointUrl
 * @property {boolean} sameOrigin
 * @property {boolean} crossOrigin
 * @property {string} expectedCredentialsMode
 * @property {boolean} csrfCookiePresent - @deprecated alias: readable in document.cookie on React origin
 * @property {import('../auth/csrfDiagnostics').CsrfCookieDiagnostic} csrfDiagnostic
 * @property {boolean} hoodieApiConfigured
 * @property {string} sessionCookieNote
 */

/**
 * @typedef {Object} EnvironmentFetchDiagnostic
 * @property {boolean} ok
 * @property {number|null} status
 * @property {string} statusText
 * @property {string} url
 * @property {string} method
 * @property {'include'|'omit'} credentials
 * @property {string|null} contentType
 * @property {string|null} responsePreview
 * @property {string|null} errorName
 * @property {string|null} errorMessage
 * @property {DiagnosticErrorCategory} errorCategory
 * @property {string} likelyCause
 * @property {string} nextAction
 * @property {unknown} [data] - parsed body (not copied to summary preview if large)
 */


export function resolveSessionEndpointUrl() {
  if (!isHoodieApiConfigured()) return null;
  return HOODIE_API_BASE_URL + SESSION_PROFILE_PATH;
}

/** @returns {EnvironmentSnapshot} */
export function getEnvironmentSnapshot() {
  const frontendOrigin =
    typeof window !== "undefined" ? window.location.origin : null;
  const backendOrigin = getDjangoSiteOrigin();
  const sessionEndpointUrl = resolveSessionEndpointUrl();
  const sameOrigin = Boolean(
    frontendOrigin && backendOrigin && frontendOrigin === backendOrigin
  );

  const csrfDiagnostic = getCsrfCookieDiagnostic();

  return {
    frontendOrigin,
    backendOrigin,
    apiBaseUrl: isHoodieApiConfigured() ? HOODIE_API_BASE_URL : null,
    sessionEndpointUrl,
    sameOrigin,
    crossOrigin: Boolean(frontendOrigin && backendOrigin && !sameOrigin),
    expectedCredentialsMode: "include",
    csrfCookiePresent: csrfDiagnostic.readableInJavaScript,
    csrfDiagnostic,
    hoodieApiConfigured: isHoodieApiConfigured(),
    sessionCookieNote:
      "sessionid is HttpOnly — JavaScript cannot read it; presence must be inferred from the server response when using credentials: include.",
  };
}

function previewResponse(data) {
  if (data == null) return null;
  try {
    const str = typeof data === "string" ? data : JSON.stringify(data);
    return str.length > RESPONSE_PREVIEW_MAX
      ? `${str.slice(0, RESPONSE_PREVIEW_MAX)}…`
      : str;
  } catch {
    return "[unserializable response]";
  }
}

function analyzeHttpFailure(status, data, credentials, crossOrigin) {
  const detail = String(data?.detail || data?.message || "").trim();

  if (status === 401) {
    return {
      errorCategory: /** @type {const} */ ("http_401"),
      likelyCause:
        "Request reached Django and returned 401 Unauthorized. Session cookie was not accepted or session expired.",
      nextAction:
        "Sign in via Django on the API domain, then retry with credentials: include. If direct tab visit works but fetch fails, suspect cross-site cookie policy.",
    };
  }

  if (status === 403) {
    if (
      detail.includes("Authentication credentials were not provided") ||
      detail.includes("not authenticated") ||
      detail.includes("sign in")
    ) {
      return {
        errorCategory: /** @type {const} */ ("http_403"),
        likelyCause: crossOrigin
          ? "Request reached Django but no session cookie was sent or accepted. You may be logged in when visiting the API URL directly in a tab, but cross-origin fetch from the React origin does not include that cookie (SameSite, Secure, Domain, or CORS credentials mismatch)."
          : "Request reached Django but session authentication was not provided.",
        nextAction:
          "Confirm REACT_APP_HOODIE_API_BASE_URL matches the domain where you logged in. On cross-origin setups, review SESSION_COOKIE_SAMESITE, SESSION_COOKIE_SECURE, cookie Domain, CORS_ALLOW_CREDENTIALS, and that Access-Control-Allow-Origin is not * when credentials are used.",
      };
    }
    return {
      errorCategory: /** @type {const} */ ("http_403"),
      likelyCause: detail
        ? `Request reached Django and returned 403: ${detail}`
        : "Request reached Django and returned 403 Forbidden.",
      nextAction:
        "Check Django permissions for hoodies/me/ and whether the session user is allowed.",
    };
  }

  return {
    errorCategory: /** @type {const} */ ("http_other"),
    likelyCause: detail
      ? `HTTP ${status}: ${detail}`
      : `HTTP ${status} from session endpoint.`,
    nextAction:
      credentials === "omit"
        ? "Session auth requires credentials: include — omit mode is diagnostic only."
        : "Inspect response body and Django logs for this status.",
  };
}

function analyzeNetworkError(err, crossOrigin) {
  const errorName = err?.name || "Error";
  const errorMessage = err?.message || String(err);

  return {
    errorCategory: /** @type {const} */ ("network"),
    errorName,
    errorMessage,
    likelyCause: crossOrigin
      ? "Browser blocked the request before a usable HTTP response (TypeError: Failed to fetch). Common causes: CORS preflight failure, Access-Control-Allow-Origin missing or set to * while credentials:include is used, missing Access-Control-Allow-Credentials, DNS/TLS/network failure, or mixed content."
      : "Browser blocked or aborted the request before a usable HTTP response. Check network tab for CORS, DNS, TLS, or connectivity errors.",
    nextAction:
      "Open DevTools → Network, retry, and look for (failed) or CORS errors on hoodies/me/. Run include vs omit tests on the staging diagnostics page. Django settings.py documents CORS_ORIGIN_ALLOW_ALL=True — wildcard ACAO blocks credentialed cross-origin fetches.",
  };
}

/**
 * GET session endpoint with structured diagnostic metadata.
 * @param {'include'|'omit'} [credentials='include']
 * @returns {Promise<EnvironmentFetchDiagnostic>}
 */
export async function diagnoseSessionFetch(credentials = "include") {
  const snapshot = getEnvironmentSnapshot();
  const url = snapshot.sessionEndpointUrl || "";
  const method = "GET";

  if (!url) {
    return {
      ok: false,
      status: null,
      statusText: "",
      url: "",
      method,
      credentials,
      contentType: null,
      responsePreview: null,
      errorName: "Unconfigured",
      errorMessage: "REACT_APP_HOODIE_API_BASE_URL is not set",
      errorCategory: "unconfigured",
      likelyCause:
        "Hoodie API base URL is missing from the React build environment.",
      nextAction:
        "Set REACT_APP_HOODIE_API_BASE_URL to the Django API origin (e.g. https://do-app.internal.themamahood.co) and rebuild.",
    };
  }

  try {
    const response = await fetch(url, {
      method,
      credentials,
      headers: { Accept: "application/json" },
    });

    const contentType = response.headers.get("content-type");
    let data = null;

    try {
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text || null;
      }
    } catch (parseErr) {
      return {
        ok: false,
        status: response.status,
        statusText: response.statusText,
        url,
        method,
        credentials,
        contentType,
        responsePreview: null,
        errorName: parseErr?.name || "ParseError",
        errorMessage: parseErr?.message || "Failed to parse response body",
        errorCategory: "parse",
        likelyCause:
          "HTTP response received but JSON/body parsing failed.",
        nextAction:
          "Inspect raw response in Network tab — may be HTML error page or proxy response.",
      };
    }

    if (!response.ok) {
      const analysis = analyzeHttpFailure(
        response.status,
        data,
        credentials,
        snapshot.crossOrigin
      );
      return {
        ok: false,
        status: response.status,
        statusText: response.statusText,
        url,
        method,
        credentials,
        contentType,
        responsePreview: previewResponse(data),
        errorName: null,
        errorMessage:
          data?.detail || data?.message || `HTTP ${response.status}`,
        data,
        ...analysis,
      };
    }

    const successNote =
      credentials === "omit" && snapshot.crossOrigin
        ? "GET succeeded without sending cookies. This does not prove session auth — use credentials: include for member session checks."
        : credentials === "include"
          ? "Session endpoint returned 200 with credentials: include."
          : "Session endpoint returned 200.";

    return {
      ok: true,
      status: response.status,
      statusText: response.statusText,
      url,
      method,
      credentials,
      contentType,
      responsePreview: previewResponse(data),
      errorName: null,
      errorMessage: null,
      errorCategory: null,
      likelyCause: successNote,
      nextAction:
        credentials === "include"
          ? "Member session check should succeed if cookies are correctly scoped."
          : "Re-run with credentials: include to verify session cookie behavior.",
      data,
    };
  } catch (err) {
    const analysis = analyzeNetworkError(err, snapshot.crossOrigin);
    return {
      ok: false,
      status: null,
      statusText: "",
      url,
      method,
      credentials,
      contentType: null,
      responsePreview: null,
      ...analysis,
    };
  }
}

/** Human-readable error category label for UI */
export function formatErrorCategory(category) {
  const labels = {
    network: "Network / fetch failure (no HTTP response)",
    http_401: "HTTP 401 Unauthorized",
    http_403: "HTTP 403 Forbidden",
    http_other: "Other HTTP error",
    parse: "Response parse error",
    unconfigured: "API base URL not configured",
  };
  return labels[category] || category || "—";
}

/**
 * Build copyable diagnostic summary (no secrets).
 * @param {EnvironmentSnapshot} snapshot
 * @param {EnvironmentFetchDiagnostic|null} [includeResult]
 * @param {EnvironmentFetchDiagnostic|null} [omitResult]
 */
export function buildDiagnosticSummary(snapshot, includeResult, omitResult) {
  const summary = {
    generatedAt: new Date().toISOString(),
    environment: {
      frontendOrigin: snapshot.frontendOrigin,
      backendOrigin: snapshot.backendOrigin,
      apiBaseUrl: snapshot.apiBaseUrl,
      sessionEndpointUrl: snapshot.sessionEndpointUrl,
      sameOrigin: snapshot.sameOrigin,
      crossOrigin: snapshot.crossOrigin,
      csrfCookiePresent: snapshot.csrfCookiePresent,
      csrfCookieJsVisibility: snapshot.csrfDiagnostic?.shortLabel,
      csrfCookieNote: snapshot.csrfDiagnostic?.detailNote,
      sessionCookieNote: snapshot.sessionCookieNote,
    },
    tests: {},
  };

  if (includeResult) {
    summary.tests.credentialsInclude = sanitizeDiagnosticForCopy(includeResult);
  }
  if (omitResult) {
    summary.tests.credentialsOmit = sanitizeDiagnosticForCopy(omitResult);
  }

  return summary;
}

function sanitizeDiagnosticForCopy(diagnostic) {
  const { data, ...rest } = diagnostic;
  return {
    ...rest,
    responsePreview: diagnostic.responsePreview,
  };
}

export function formatDiagnosticSummaryText(snapshot, includeResult, omitResult) {
  return JSON.stringify(
    buildDiagnosticSummary(snapshot, includeResult, omitResult),
    null,
    2
  );
}
